<template>
	<div v-if="hasFilters" class="row">
		<div class="flex w-full flex-wrap justify-center gap-4">
			<div
				v-if="dateRange"
				class="tenant-div flex w-auto items-center justify-between rounded-full bg-blue-500 px-4 text-white"
				:class="{ 'bg-red-500': hover === 'dates', 'hover:bg-blue-600': hover !== 'dates' }"
			>
				<p class="px-2 py-1 text-sm">
					<span class="mr-1 font-bold uppercase">date:</span>
					{{
						formatDate(searchParams.dateAddedFrom) +
						(searchParams.dateAddedTo ? ' to ' + formatDate(searchParams.dateAddedTo) : ' to now')
					}}
				</p>
				<span
					class="fa-light fa-close ml-2 cursor-pointer"
					@mouseover="hover = 'dates'"
					@mouseleave="hover = null"
					@click="$emit('clear-dates')"
				></span>
			</div>

			<div
				v-for="pill in pills"
				:key="pill.category + pill.name"
				class="tenant-div flex w-auto items-center justify-between rounded-full bg-blue-500 px-4 text-white"
				:class="{
					'bg-red-500': hover === pill.category.toLowerCase(),
					'hover:bg-blue-600': hover !== pill.category.toLowerCase()
				}"
			>
				<p class="px-2 py-1 text-sm">
					<span class="mr-1 font-bold uppercase">{{ pill.category.toLowerCase() }}:</span>
					{{ formatPillValue(pill) }}
				</p>
				<span
					class="fa-light fa-close ml-2 cursor-pointer"
					@mouseover="hover = pill.category.toLowerCase()"
					@mouseleave="hover = null"
					@click="$emit('remove-pill', pill)"
				></span>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment'

export default {
	props: {
		searchParams: {
			type: Object,
			required: true
		},
		tenants: {
			type: Array,
			default: () => []
		},
		accounts: {
			type: Array,
			default: () => []
		}
	},

	emits: ['clear-dates', 'remove-pill'],

	data() {
		return {
			hover: null,
			pills: []
		}
	},

	computed: {
		hasFilters() {
			return (
				this.searchParams.dateAddedFrom ||
				this.searchParams.dateAddedTo ||
				this.searchParams.carrier ||
				(this.searchParams.selectedTenant && Object.keys(this.searchParams.selectedTenant).length) ||
				(this.searchParams.selectedAccount && Object.keys(this.searchParams.selectedAccount).length) ||
				this.searchParams.callFrom ||
				this.searchParams.callTo ||
				this.searchParams.callAnywhere ||
				(this.searchParams.hangupCodes && this.searchParams.hangupCodes.length) ||
				(this.searchParams.zoneNames && this.searchParams.zoneNames.length) ||
				this.searchParams.callDurationFrom ||
				this.searchParams.callDurationTo ||
				this.searchParams.billableDurationFrom ||
				this.searchParams.billableDurationTo ||
				this.searchParams.withRecording ||
				this.searchParams.withTranscription ||
				this.searchParams.withVoicemail
			)
		},

		dateRange() {
			return this.searchParams.dateAddedFrom || this.searchParams.dateAddedTo
		}
	},

	watch: {
		searchParams: {
			deep: true,
			immediate: true,
			handler(newParams) {
				this.updatePills()
			}
		}
	},

	created() {
		this.updatePills()
	},

	methods: {
		formatDate(date) {
			return date ? moment(date).format('DD/MM/YYYY') : 'now'
		},

		updatePills() {
			this.pills = []

			if (this.searchParams.carrier) {
				const displayName = this.searchParams.carrierName || this.searchParams.carrier

				this.pills.push({
					category: 'Carrier',
					name: displayName
				})
			}

			if (this.searchParams.selectedTenant?.companyName && this.tenants?.length > 1) {
				this.pills.push({ category: 'Tenant', name: this.searchParams.selectedTenant.companyName })
			}

			if (this.searchParams.selectedAccount?.name && this.accounts?.length > 1) {
				this.pills.push({ category: 'Account', name: this.searchParams.selectedAccount.name })
			}

			if (this.searchParams.callAnywhere) {
				this.pills.push({ category: 'Call Anywhere', name: this.searchParams.callAnywhere })
			} else {
				if (this.searchParams.callFrom) {
					this.pills.push({ category: 'Source', name: this.searchParams.callFrom })
				}

				if (this.searchParams.callTo) {
					this.pills.push({ category: 'Destination', name: this.searchParams.callTo })
				}
			}

			if (this.searchParams.withRecording) {
				this.pills.push({ category: 'With Recording', name: 'Yes' })
			}

			if (this.searchParams.withTranscription) {
				this.pills.push({ category: 'With Transcription', name: 'Yes' })
			}

			if (this.searchParams.withVoicemail) {
				this.pills.push({ category: 'With Voicemail', name: 'Yes' })
			}

			if (this.searchParams.hangupCodes?.length) {
				this.pills.push({
					category: 'Hangup Code',
					name: this.searchParams.hangupCodes.join(', '),
					count: this.searchParams.hangupCodes.length
				})
			}

			if (this.searchParams.zoneNames?.length) {
				this.pills.push({
					category: 'Zone',
					name: this.searchParams.zoneNames.join(', '),
					count: this.searchParams.zoneNames.length
				})
			}

			if (this.searchParams.callDurationFrom || this.searchParams.callDurationTo) {
				this.pills.push({
					category: 'Duration',
					name: 'duration',
					from: this.searchParams.callDurationFrom,
					to: this.searchParams.callDurationTo
				})
			}

			if (this.searchParams.billableDurationFrom || this.searchParams.billableDurationTo) {
				this.pills.push({
					category: 'Billable Duration',
					name: 'billableDuration',
					from: this.searchParams.billableDurationFrom,
					to: this.searchParams.billableDurationTo
				})
			}
		},

		formatPillValue(pill) {
			if (pill.count && pill.count > 3) {
				return pill.name.split(',').slice(0, 3).join(', ') + ` + ${pill.count - 3} more`
			}

			if (pill.from !== undefined || pill.to !== undefined) {
				return `${!pill.to ? '> ' : ''}${pill.from ? pill.from + 's' : ''}${
					pill.from && pill.to ? ' - ' : ''
				}${!pill.from ? '< ' : ''}${pill.to ? pill.to + 's' : ''}`
			}

			return pill.name
		}
	}
}
</script>
